import React, { useState } from 'react';
import {
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@roger/ui-components';
import type {
  GoalProgressDetail,
  GoalProgressScore,
} from '../../services/api/types.gen';
import { GOALPROGRESSSCORE_VALUES } from '../../services/api/models/GoalProgressScore';
import { recordGoalProgress } from '../../services/api';
import { useToast } from '../../hooks/useToastContext';

interface GoalPerformanceBoxProps {
  visitId: string;
  patientGoalId: string;
  /** Progress already recorded on this visit; the form opens prefilled from it. */
  existingProgress?: GoalProgressDetail | null;
  onCancel: () => void;
  onSaveSuccess: () => void;
}

const GoalPerformanceBox: React.FC<GoalPerformanceBoxProps> = ({
  visitId,
  patientGoalId,
  existingProgress,
  onCancel,
  onSaveSuccess,
}) => {
  const { error: showError } = useToast();
  const [progressScore, setProgressScore] = useState<GoalProgressScore | ''>(
    existingProgress?.progress_score ?? ''
  );
  const [progressNote, setProgressNote] = useState(
    existingProgress?.progress_note ?? ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = () => {
    if (!progressScore) return;
    setIsSubmitting(true);
    recordGoalProgress({
      path: { visit_id: visitId, patient_goal_id: patientGoalId },
      body: {
        progress_score: progressScore,
        progress_note: progressNote || undefined,
      },
      throwOnError: true,
    })
      .then(() => {
        onSaveSuccess();
      })
      .catch(() => {
        showError('Failed to record goal progress');
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <div className='relative space-y-2 rounded border border-border bg-muted/30 p-2 pr-10 text-xs'>
      <button
        type='button'
        onClick={onCancel}
        className='absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-sm text-lg text-muted-foreground hover:bg-muted hover:text-foreground'
        aria-label='Close'
      >
        ×
      </button>
      <div>
        <Label
          htmlFor={`performance-${patientGoalId}`}
          className='text-muted-foreground'
        >
          Performance
        </Label>
        <Select
          value={progressScore}
          onValueChange={v => setProgressScore(v as GoalProgressScore)}
        >
          <SelectTrigger
            id={`performance-${patientGoalId}`}
            selectSize='sm'
            className='mt-1'
          >
            <SelectValue placeholder='Select performance' />
          </SelectTrigger>
          <SelectContent>
            {GOALPROGRESSSCORE_VALUES.map(val => (
              <SelectItem key={val} value={val}>
                {val}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label
          htmlFor={`note-${patientGoalId}`}
          className='text-muted-foreground'
        >
          Progress Note (Optional)
        </Label>
        <Textarea
          id={`note-${patientGoalId}`}
          value={progressNote}
          onChange={e => setProgressNote(e.target.value)}
          placeholder='Add notes...'
          textareaSize='sm'
          className='mt-1 min-h-[60px]'
        />
      </div>
      <div className='flex justify-end'>
        <Button
          size='sm'
          onClick={handleSubmit}
          disabled={!progressScore || isSubmitting}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default GoalPerformanceBox;
